import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, MessageSquareText, RefreshCw } from "lucide-react";
import Auth from "../../../utils/auth";

const SENTIMENT_META = {
  very_happy: { emoji: "😄", label: "Very Happy", chipClass: "bg-success-subtle text-success-emphasis" },
  happy: { emoji: "🙂", label: "Happy", chipClass: "bg-success-subtle text-success-emphasis" },
  neutral: { emoji: "😐", label: "Neutral", chipClass: "bg-warning-subtle text-warning-emphasis" },
  unhappy: { emoji: "🙁", label: "Unhappy", chipClass: "bg-warning-subtle text-warning-emphasis" },
  very_unhappy: { emoji: "😠", label: "Very Unhappy", chipClass: "bg-danger-subtle text-danger-emphasis" },
};

function authHeaders() {
  const token = Auth.getToken();
  return {
    "Content-Type": "application/json",
    authorization: token ? `Bearer ${token}` : "",
  };
}

export default function AdminFeedbackPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState(null);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = filter === "all" ? "" : `?status=${filter}`;
      const res = await fetch(`/api/feedback${query}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Unable to load feedback.");
      }
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Unable to load feedback.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const pendingCount = useMemo(() => items.filter((item) => item.status === "pending").length, [items]);

  const handleMarkReviewed = async (id) => {
    setActingId(id);
    setError(null);
    try {
      const res = await fetch(`/api/feedback/${id}/review`, {
        method: "PUT",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Unable to mark feedback as reviewed.");
      }

      const updated = await res.json();
      setItems((prev) => prev.map((item) => (item.id === id ? updated : item)));
    } catch (err) {
      setError(err?.message || "Unable to mark feedback as reviewed.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="container-fluid px-0 py-2">
      <div className="d-flex align-items-center gap-3 mb-3">
        <div className="bg-info-subtle border border-info rounded-3 p-2">
          <MessageSquareText size={20} className="text-info-emphasis" />
        </div>
        <div>
          <h2 className="h4 mb-1">Admin Feedback Inbox</h2>
          <p className="mb-0 text-body-secondary">Review feedback submitted by signed-in users.</p>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded-3 p-3 bg-body-tertiary mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold text-body-secondary">Filter:</span>
          {["pending", "reviewed", "all"].map((value) => {
            const selected = filter === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`btn btn-sm ${selected ? "btn-primary" : "btn-outline-secondary"} text-capitalize`}
              >
                {value}
              </button>
            );
          })}
          {filter !== "pending" && pendingCount > 0 && (
            <span className="badge text-bg-warning">{pendingCount} pending in this view</span>
          )}
        </div>

        <button
          type="button"
          onClick={loadFeedback}
          disabled={loading}
          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          ) : (
            <RefreshCw size={14} />
          )}
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-body-secondary py-4">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          Loading feedback...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-5 text-body-secondary border rounded-3">
          <CheckCircle2 size={28} className="mb-2 opacity-50" />
          <div>No feedback items found for this filter.</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {items.map((item) => {
            const sentiment = SENTIMENT_META[item.sentiment] || SENTIMENT_META.neutral;
            const isPending = item.status === "pending";
            const isActing = actingId === item.id;
            return (
              <article key={item.id} className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <span className={`badge rounded-pill ${sentiment.chipClass}`}>
                      <span className="me-1" aria-hidden="true">{sentiment.emoji}</span>
                      {sentiment.label}
                    </span>
                    <span className={`badge rounded-pill ${isPending ? "text-bg-warning" : "text-bg-success"}`}>
                      {item.status}
                    </span>
                    <span className="ms-auto small text-body-secondary">
                      {(item.submitted_by_name || item.submitted_by_email || item.submitted_by) || "Unknown user"} · {new Date(item.submitted_at).toLocaleString()}
                    </span>
                  </div>

                  <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
                    {item.feedback_text?.trim() || "No additional written feedback provided."}
                  </p>

                  <div className="d-flex flex-wrap align-items-center gap-2 small text-body-secondary mb-2">
                    {item.page_context ? <span className="badge text-bg-light">Context: {item.page_context}</span> : null}
                    {item.reviewed_by_name ? <span className="badge text-bg-light">Reviewed by: {item.reviewed_by_name}</span> : null}
                    {item.reviewed_at ? <span className="badge text-bg-light">Reviewed: {new Date(item.reviewed_at).toLocaleString()}</span> : null}
                  </div>

                  {isPending && (
                    <button
                      type="button"
                      onClick={() => handleMarkReviewed(item.id)}
                      disabled={isActing}
                      className="btn btn-sm btn-success d-inline-flex align-items-center gap-2"
                    >
                      {isActing ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <CheckCircle2 size={14} />}
                      Mark reviewed
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
